import { useState } from 'react';
import { api } from '../lib/api.js';
import { useFormFields } from '../hooks/useFormFields.js';
import { ImageCapture } from './ImageCapture.js';

export interface Personnel {
  id: string;
  full_name: string;
  designation: string;
  role?: 'trainer' | 'competent_person';
  qualification?: string;
  competency_cert_number?: string;
  competency_valid_from?: string;
  competency_valid_upto?: string;
  issuing_authority?: string;
  signature_image_url?: string | null;
  stamp_image_url?: string | null;
  competency_cert_url?: string | null;
}

interface InlinePersonnelFormProps {
  initialRole?: 'competent_person' | 'trainer';
  initialFullName?: string;
  onCreated: (personnel: Personnel) => void;
  onCancel: () => void;
  title?: string;
}

export function InlinePersonnelForm({
  initialRole = 'competent_person',
  initialFullName = '',
  onCreated,
  onCancel,
  title,
}: InlinePersonnelFormProps) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [certFileName, setCertFileName] = useState('');

  const isTrainerRole = initialRole === 'trainer';

  const fields = useFormFields({
    full_name: initialFullName,
    designation: isTrainerRole ? 'Trainer' : 'Competent Person',
    role: initialRole,
    qualification: isTrainerRole ? '' : 'BE (MECHANICAL)',
    competency_cert_number: '',
    competency_valid_from: '',
    competency_valid_upto: '',
    issuing_authority: 'Directorate of Industrial Safety and Health (DISH), Gujarat',
    signature_image_url: '',
    stamp_image_url: '',
    competency_cert_url: '',
  });

  const handleCertFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCertFileName(file.name);
    const reader = new FileReader();
    reader.onloadend = () => {
      fields.setField('competency_cert_url', reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!fields.values.full_name.trim()) {
      setError('Please enter Full Name.');
      return;
    }

    if (!isTrainerRole) {
      if (!fields.values.competency_cert_number.trim()) {
        setError('Please enter Competency Certificate Number.');
        return;
      }
      if (!fields.values.competency_valid_from.trim()) {
        setError('Please select Licence Valid From date.');
        return;
      }
      if (!fields.values.competency_valid_upto.trim()) {
        setError('Please select Licence Valid Upto date.');
        return;
      }
    }

    setSaving(true);
    setError('');

    const payload: Record<string, unknown> = {
      ...fields.values,
      role: initialRole,
      full_name: fields.values.full_name.trim(),
      designation: fields.values.designation.trim() || (isTrainerRole ? 'Trainer' : 'Competent Person'),
    };

    try {
      const res = await api.personnel.create(payload);
      const created = (res as { data?: Personnel }).data;
      if (created) {
        onCreated(created);
      } else {
        setError('Failed to create personnel');
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to create personnel');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-xl border border-purple-300 bg-purple-50/60 p-4 sm:p-5 space-y-4 shadow-sm">
      <div className="flex items-center justify-between border-b border-purple-200/80 pb-3">
        <div className="flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-full bg-purple-600 animate-pulse" />
          <h4 className="text-sm font-bold text-purple-950">
            {title || `Create New ${isTrainerRole ? 'Faculty / Trainer' : 'Competent Person'}`}
          </h4>
          <span className="text-[11px] font-semibold text-purple-700 bg-purple-100 px-2 py-0.5 rounded border border-purple-200">
            {isTrainerRole ? 'Role: Trainer / Faculty' : 'Role: Competent Person'}
          </span>
        </div>
        <button
          type="button"
          onClick={onCancel}
          className="text-xs font-semibold text-purple-600 hover:text-purple-800 bg-white hover:bg-purple-100 px-2 py-1 rounded border border-purple-200 transition-colors"
        >
          ✕ Close Form
        </button>
      </div>

      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 px-3.5 py-2 text-xs font-semibold text-red-700 flex items-center justify-between">
          <span>{error}</span>
          <button type="button" onClick={() => setError('')} className="text-red-500 hover:text-red-700">✕</button>
        </div>
      )}

      {/* Basic Info */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-semibold text-gray-800 mb-1">
            Full Name <span className="text-red-500">*</span>
          </label>
          <input
            className="input bg-white text-xs"
            {...fields.bind('full_name')}
            placeholder="e.g. Mr. Rajesh Kumar"
            required
          />
        </div>
        {!isTrainerRole ? (
          <div>
            <label className="block text-xs font-semibold text-gray-800 mb-1">
              Qualification <span className="text-red-500">*</span>
            </label>
            <input
              className="input bg-white text-xs"
              {...fields.bind('qualification')}
              placeholder="e.g. M.TECH (PRODUCTION) BE (MECHANICAL)"
              required
            />
          </div>
        ) : (
          <div>
            <label className="block text-xs font-semibold text-gray-800 mb-1">Designation</label>
            <input
              className="input bg-white text-xs"
              {...fields.bind('designation')}
              placeholder="e.g. Trainer"
            />
          </div>
        )}
      </div>

      {/* Competency Cert Details for Competent Person */}
      {!isTrainerRole && (
        <div className="space-y-3 pt-1">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-800 mb-1">
                Competency Cert Number <span className="text-red-500">*</span>
              </label>
              <input
                className="input bg-white text-xs font-mono"
                {...fields.bind('competency_cert_number')}
                placeholder="e.g. GUJ/DISH/CPTA/A-1401/2024"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-800 mb-1">
                Licence Valid From <span className="text-red-500">*</span>
              </label>
              <input
                className="input bg-white text-xs"
                type="date"
                {...fields.bind('competency_valid_from')}
                required
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-800 mb-1">
                Licence Valid Upto <span className="text-red-500">*</span>
              </label>
              <input
                className="input bg-white text-xs"
                type="date"
                {...fields.bind('competency_valid_upto')}
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-800 mb-1">Issuing Authority</label>
              <input
                className="input bg-white text-xs"
                {...fields.bind('issuing_authority')}
                placeholder="e.g. Director, Industrial Safety & Health, Gujarat State"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-800 mb-1">Competency Cert File (PDF/Image)</label>
              <input
                type="file"
                accept="application/pdf,image/*"
                onChange={handleCertFileSelect}
                className="input bg-white text-xs cursor-pointer py-1"
              />
              {certFileName && (
                <p className="text-[11px] text-brand-700 font-medium mt-0.5">Attached: {certFileName}</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Signature & Stamp Captures */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
        <ImageCapture
          key="inline-signature"
          label="Signature"
          onChange={(dataUrl) => fields.setField('signature_image_url', dataUrl)}
        />
        {!isTrainerRole && (
          <ImageCapture
            key="inline-stamp"
            label="Stamp"
            hideDraw
            onChange={(dataUrl) => fields.setField('stamp_image_url', dataUrl)}
          />
        )}
      </div>

      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 px-3.5 py-2 text-xs font-semibold text-red-700 flex items-center justify-between my-2">
          <span>{error}</span>
          <button type="button" onClick={() => setError('')} className="text-red-500 hover:text-red-700">✕</button>
        </div>
      )}

      {/* Action Footer */}
      <div className="flex items-center justify-end gap-2 pt-2 border-t border-purple-200/80">
        <button
          type="button"
          onClick={onCancel}
          className="btn btn-secondary text-xs"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => handleSubmit()}
          disabled={saving}
          className="btn btn-primary text-xs bg-purple-700 hover:bg-purple-800 border-purple-700 flex items-center gap-1.5"
        >
          {saving && <span className="inline-block animate-spin rounded-full h-3.5 w-3.5 border-2 border-white border-t-transparent shrink-0" />}
          {saving ? 'Creating Personnel…' : 'Save Personnel & Select'}
        </button>
      </div>
    </div>
  );
}
